import { dehydrate, QueryClient } from '@tanstack/react-query'
import omitEmptyOrNil from '@vangst/lib/omitEmptyOrNil'
import { fetchPage } from '@vangst/services/oogst/page'
import {
  ElasticSearchEndpoints,
  ElasticSearchVars,
} from '@vangst/services/oogst/search/useElasticSearch'
import { RobotsEnum } from '@vangst/services/oogst/types'
import Routes from '@vangst/services/routes'
import { InferGetStaticPropsType } from 'next'
import { MdDescription } from 'react-icons/md'
import Breadcrumbs from '../../../components/navigation/Breadcrumbs'
import ClickyLink from '../../../components/navigation/ClickyLink'
import GroupHeader from '../../../components/views/GroupHeader'
import Markdown from '../../../components/views/Markdown'
import PageHeader from '../../../components/views/PageHeader'
import Meta from '../../../domains/application/Meta'
import ConnectSearchSidebar from '../../../domains/marketing/ConnectSearchSidebar'
import StateRequirementsHeaderMap from '../../../domains/marketing/StateRequirementsHeaderMap'
import StateRequirementsHeaderSelect from '../../../domains/marketing/StateRequirementsHeaderSelect'
import SearchRenderer from '../../../domains/search/SearchRenderer'

type SearchType = {
  title: string
  endpoint: ElasticSearchEndpoints
  variables: ElasticSearchVars
}

export async function getStaticProps() {
  const queryClient = new QueryClient()
  const rawUrl = __filename.split('pages')[1]?.replace('.js', '')
  const { getPage } = await fetchPage(queryClient, { rawUrl })
  const page = {
    ...getPage,
    title: getPage?.title ?? 'Cannabis Job Requirements By State',
  }

  const crumbs = [
    { href: Routes.INDEX, text: 'Home' },
    { href: Routes.REPORTS, text: 'Reports' },
    { href: null, text: 'State Requirements' },
  ]

  const search: SearchType = {
    title: 'Latest Jobs',
    endpoint: 'searchJobPostings',
    variables: {
      first: 6,
    },
  }

  return {
    props: {
      dehydratedState: omitEmptyOrNil(dehydrate(queryClient), true),
      crumbs,
      page,
      search,
    },
    revalidate: 30,
  }
}

// -------------------------------------

type PropsType = InferGetStaticPropsType<typeof getStaticProps>

export default function StateRequirementsPage(props: PropsType) {
  const { crumbs, page, search } = props

  return (
    <>
      <Meta
        key="meta"
        canonicalUrl={page.metaInfo?.canonicalUrl}
        crumbs={crumbs}
        description={page.metaInfo?.description}
        image={page.pagePhotos?.[0]?.url}
        publishDate={page.postedAt}
        robots={page.metaInfo?.robots ?? RobotsEnum.IndexFollow}
        title={page.metaInfo?.title ?? page.title}
      />
      <Breadcrumbs crumbs={crumbs} />
      <article className="mood-white">
        <PageHeader
          accent="text-blue"
          icon="pin drop"
          title={page.title}
          subtitle={page.subtitle}
          description={page.description}
          imgAlt={page.pagePhotos?.[0]?.alt}
          imgSrc={page.pagePhotos?.[0]?.url}
        />
      </article>
      <StateRequirementsHeaderMap />
      <StateRequirementsHeaderSelect />
      {page.markdownContent && (
        <section className="contain-xyc mood-white">
          <div className="text-sm">
            <Markdown renderType="basic">{page.markdownContent}</Markdown>
          </div>
        </section>
      )}
      <div className="contain-xyc mood-white grid gap-8 py-16">
        <div className="grid content-start gap-6 sm:grid-cols-[3fr_1fr] sm:gap-12">
          <div className="grid content-start gap-4">
            <p>Cannabis Jobs are Hiring!</p>
            <p>
              In the cannabis industry, change is the only constant. If you
              thrive in that kind of dynamic, full-of-opportunity environment,
              this is absolutely the place to be. But breaking in can be tricky
              – especially since every state has its own unique set of
              requirements to work in cannabis. We know the laws are nuanced and
              can even feel confusing (thank you, Uncle Sam!). But learning all
              about the regulations in your state is an essential first step on
              your cannabis career journey. In fact, our recruiting experts will
              tell you the more you know, the more it’ll help you stand out with
              potential employers.
            </p>
            <blockquote className="grid gap-2 border-l-2 border-grey-light pl-2">
              <em>
                The best way to stand out for a job in the cannabis industry is
                by being genuine about why you want a cannabis job and begin
                educating yourself on what that transition will look like. Learn
                about the cannabis industry, understand the regulations, and
                start networking with advocates in your area. The barrier to
                entry into a cannabis job may be high, but by setting yourself
                apart from the average applicant, you’ll be able to land a
                cannabis job with a great team.
              </em>
              <cite>- Kelsea, Head of Community Partnerships at Vangst</cite>
            </blockquote>
            <div>
              <p>
                So, where to start learning about Cannabis Jobs? As you explore
                this guide you see there are three types of states in the US:
              </p>
              <ul className="ml-4 mt-2 list-disc">
                <li>Adult use states</li>
                <li>Medical states</li>
                <li>Illegal states</li>
              </ul>
            </div>
            <p>
              What's the difference? “Recreational marijuana" (or “adult-use
              cannabis”), is legal marijuana sold in licensed dispensaries to
              any adult ages 21 and older. This might come in any variety of
              forms, from smokable flowers to vape cartridges to edibles and
              more. “Medical marijuana,” on the other hand, is only legal for
              patients who maintain a valid medical cannabis card. States where
              adult use cannabis is decriminalized but where sales are currently
              not regulated or in the process of being regulated will not appear
              marked on our map. Additional information on decriminalization is
              available within each state page where applicable.
            </p>
            <p>
              You’ll also notice that not all companies in the cannabis industry
              require state licenses, but most growers, processors, and
              retailers do. If you’re considering a company that touches the
              plant, it’s important to work only with licensed operators in the
              cannabis industry.
            </p>
            <p>
              Explore the unique laws and regulations in your state by clicking
              into the tool above. Still have questions? You’re not alone.{' '}
              <ClickyLink
                className="link-orange underline"
                href={Routes.BLOG_VANGST}
              >
                Check out our additional resources
              </ClickyLink>
              . The Vangst community is here to help you along your cannabis
              career journey – every step of the way.
            </p>
            <hr className="my-4" />
            <div className="mt-4">
              <GroupHeader
                className="mb-8"
                icon={MdDescription}
                title={search.title}
                to={Routes.JOBS}
                toLabel="Search All Jobs"
              >
                <span className="mt-4 lg:ml-8">
                  Search a vast pool of cannabis job openings catering to
                  everyone from enthusiasts to experts.
                </span>
              </GroupHeader>
              <SearchRenderer
                as="card"
                at="infinite-preview"
                classNameList="grid gap-16 md:grid-cols-3"
                endpoint={search.endpoint}
                variables={search.variables}
              />
            </div>
          </div>
          <ConnectSearchSidebar />
        </div>
      </div>
    </>
  )
}
